import { Playlist } from '@/core/api/entities/playlist/playlist'
import { FeedbackType } from '@/core/api/entities/song/feedbackType'
import { Song } from '@/core/api/entities/song/song'
import { SongFeedback } from '@/core/api/entities/song/songFeedback'
import useTranslation from '@/core/i18n/useTranslation'
import {
	HandThumbsDown,
	HandThumbsUp,
} from '@/features/music/components/HandThumbs'
import { usePlaylistTracking } from '@/shared/analytics/usePlaylistTracking'
import Icon from '@/shared/components/atoms/Icon'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { cn } from '@/shared/utils/helpers'

// Utility function to format seconds to MM:SS
const formatDuration = (seconds: number): string => {
	const mins = Math.floor(seconds / 60)
	const secs = Math.floor(seconds % 60)
	return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

interface PlaylistSongsProps {
	playlist: Playlist
	searchQuery: string
	filteredSongs: Song[]
	songFeedbacks: Map<number, SongFeedback>
	feedbackTypes: FeedbackType[]
	isLoadingFeedback: boolean
	currentPlayingSong: Song | null
	hoveredFeedbackSongId: number | null
	playlistFeedbacks: SongFeedback[]
	allFeedbackTypes: FeedbackType[]
	selectedSongForFeedback: number | null
	isSubmittingFeedback: boolean
	isPlayerPlaying: boolean
	setIsPlayerPlaying: (isPlaying: boolean) => void
	handleFeedbackClick: (song: Song, isLike: boolean) => void
	setHoveredFeedbackSongId: (songId: number | null) => void
	setCurrentPlayingSong: (song: Song | null) => void
}

export const PlaylistSongs = ({
	playlist,
	searchQuery,
	filteredSongs,
	songFeedbacks,
	feedbackTypes,
	isLoadingFeedback,
	currentPlayingSong,
	hoveredFeedbackSongId,
	playlistFeedbacks,
	allFeedbackTypes,
	isSubmittingFeedback,
	selectedSongForFeedback,
	isPlayerPlaying,
	setIsPlayerPlaying,
	handleFeedbackClick,
	setHoveredFeedbackSongId,
	setCurrentPlayingSong,
}: PlaylistSongsProps) => {
	const { t, currentLanguage } = useTranslation('common')
	const { trackSongPlay } = usePlaylistTracking()

	// Function to aggregate feedback data for a song
	const getSongFeedbackStats = (songId: number) => {
		const allFeedbackData =
			playlistFeedbacks?.filter(
				(feedback: SongFeedback) =>
					feedback.songId === songId && feedback.playerId === null
			) || []

		// Filter feedback for this song and exclude feedback where player_id is null
		const songFeedbacks = allFeedbackData.filter((feedback: any) => {
			const id = feedback.song_id || feedback.songId || feedback.song?.id
			const playerId = feedback.player_id || feedback.playerId
			// Only include feedback that matches the song AND has a non-null player_id
			return id === songId && playerId != null
		})

		let likesCount = 0
		let dislikesCount = 0
		const dislikeReasons: Map<string, number> = new Map()

		songFeedbacks.forEach((feedback: any) => {
			const typeId =
				feedback.type_id ||
				feedback.feedback_type_id ||
				feedback.type?.id ||
				feedback.feedback_type?.id

			const feedbackType = allFeedbackTypes.find((type) => type.id === typeId)

			if (feedbackType) {
				// Use is_negative flag to determine if it's a like or dislike
				if (feedbackType.isNegative) {
					dislikesCount++
					// Get translated reason name
					const getTranslatedReason = () => {
						if (feedbackType.titleTranslations && currentLanguage) {
							if (feedbackType.titleTranslations[currentLanguage]) {
								return feedbackType.titleTranslations[currentLanguage]
							}
							const baseLanguage = currentLanguage?.split('-')[0]
							if (baseLanguage === 'es') {
								return (
									feedbackType.titleTranslations['es-ES'] ||
									feedbackType.titleTranslations['es-419'] ||
									feedbackType.name
								)
							} else if (baseLanguage === 'en') {
								return (
									feedbackType.titleTranslations['en-US'] || feedbackType.name
								)
							}
						}
						return feedbackType.name || t('entities.music.unknown_reason')
					}
					const reasonName = getTranslatedReason()
					dislikeReasons.set(
						reasonName,
						(dislikeReasons.get(reasonName) || 0) + 1
					)
				} else {
					// Positive feedback (is_negative === false)
					likesCount++
				}
			}
		})

		return {
			likesCount,
			dislikesCount,
			dislikeReasons: Array.from(dislikeReasons.entries()).map(
				([reason, count]) => ({ reason, count })
			),
		}
	}

	return (
		<div className="w-full">
			{searchQuery.trim() && filteredSongs.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-8 px-4 min-h-[300px]">
					<div className="flex flex-col items-center justify-center text-center gap-4 max-w-[400px]">
						<div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-2">
							<Icon
								name="search"
								width="32px"
								height="32px"
								color="var(--foreground)"
							/>
						</div>
						<h3 className="text-lg font-medium text-foreground m-0">
							{t('entities.music.no_songs')}
						</h3>
						<p className="text-base text-muted-foreground m-0">
							{t('entities.music.search_no_results_description')}
						</p>
					</div>
				</div>
			) : (
				<table className="w-full border-collapse table-fixed">
					<thead className="mb-2">
						<tr>
							<th className="hidden lg:table-cell lg:p-4 lg:w-[32%] text-left text-sm font-medium text-foreground">
								{t('entities.music.song')}
							</th>
							<th className="hidden lg:table-cell p-4 lg:w-[32%] text-left text-sm font-medium text-foreground">
								{t('entities.music.artist_name')}
							</th>
							<th className="hidden lg:table-cell p-4 text-left text-sm font-medium text-foreground">
								{t('entities.music.length')}
							</th>
							<th className="hidden lg:table-cell p-4 text-left text-sm font-medium text-foreground">
								{t('entities.music.feedback')}
							</th>
							<th className="hidden lg:table-cell p-4 text-left text-sm font-medium text-foreground">
								{t('entities.music.your_feedback')}
							</th>
						</tr>
					</thead>
					<tbody className="w-full">
						{filteredSongs.map((song) => {
							// Get feedback data for this song
							const songFeedback = songFeedbacks.get(song.id)
							const feedbackType = songFeedback?.feedbackType

							const isSelectedSong = selectedSongForFeedback === song.id

							// Determine icon and category based on is_negative flag
							const getFeedbackIcon = () => {
								if (!feedbackType) return null

								// Use is_negative flag to determine icon
								if (feedbackType.isNegative) {
									return 'feedback_bad'
								} else {
									return 'feedback_good'
								}
							}

							const feedbackIcon = getFeedbackIcon()

							// Determine feedback category based on is_negative flag
							const getFeedbackCategory = ():
								| 'success'
								| 'warning'
								| 'error' => {
								if (!feedbackType) return 'success' // Default to success

								if (feedbackType.isNegative) {
									return 'error'
								} else {
									return 'success'
								}
							}

							const feedbackCategory = getFeedbackCategory()

							// Get background color based on feedback category
							const getFeedbackBackground = (
								category: 'success' | 'warning' | 'error'
							) => {
								switch (category) {
									case 'success':
										return 'rgba(16, 185, 129, 0.1)' // #10B981 with 10% opacity
									case 'warning':
										return 'rgba(245, 158, 11, 0.1)' // #F59E0B with 10% opacity
									case 'error':
										return 'rgba(237, 90, 70, 0.1)' // #ED5A46 with 10% opacity
									default:
										return 'rgba(16, 185, 129, 0.1)' // Default to success
								}
							}

							// Get icon color based on feedback category
							const getFeedbackIconColor = () => {
								// If there's feedback, use the category color
								if (feedbackType) {
									switch (feedbackCategory) {
										case 'success':
											return '#10B981' // Green for success
										case 'warning':
											return '#F59E0B' // Yellow/orange for warning
										case 'error':
											return '#ED5A46' // Red for error
										default:
											return '#10B981' // Default to success
									}
								}
								// Default color when no feedback
								return '#10B981'
							}

							const handleSongRowClick = () => {
								// Track song play event
								trackSongPlay(
									{
										playlistId: playlist.id,
										playlistTitle: playlist.title,
									},
									{
										songId: song.id,
										songTitle: song.metadata.title,
										artistName: song.metadata.artist,
									}
								)

								// Set the current playing song for the Player component
								setCurrentPlayingSong(song)

								// TrackPlayer handles playback when currentPlayingSong changes.
							}

							// Check if this song is currently playing or paused
							const isCurrentSong = currentPlayingSong?.id === song.id
							const isCurrentlyPlaying = isCurrentSong && isPlayerPlaying
							const isCurrentlyPaused = isCurrentSong && !isPlayerPlaying
							const shouldShowIcon = isCurrentlyPlaying || isCurrentlyPaused // Always visible when playing/paused

							// Handle icon click to toggle play/pause
							const handleIconClick = (e: React.MouseEvent) => {
								e.stopPropagation() // Prevent row click

								if (isCurrentSong) {
									// Same song - toggle play/pause
									// Update state immediately for better UX
									if (isCurrentlyPlaying) {
										setIsPlayerPlaying(false)
									} else {
										setIsPlayerPlaying(true)
									}

									// Click the play/pause button in the player
									const playPauseButton = document.querySelector(
										'.rhap_play-pause-button'
									) as HTMLButtonElement
									if (playPauseButton) {
										playPauseButton.click()
									} else {
										// Fallback: use audio element directly
										const audioElement = document.querySelector(
											'.rhap_audio-element'
										) as HTMLAudioElement
										if (audioElement) {
											if (isCurrentlyPlaying) {
												audioElement.pause()
											} else {
												audioElement.play().catch((error) => {
													console.error('Error playing audio:', error)
												})
											}
										}
									}
								} else {
									// Different song - play it
									handleSongRowClick()
								}
							}

							return (
								<tr
									key={song.id}
									onClick={handleSongRowClick}
									className="flex px-4 md:pr-10 py-4 flex-col lg:flex-none lg:table-row border-b last:border-b-0 cursor-pointer transition-colors duration-200 hover:bg-border/5 group"
								>
									<td className="lg:p-4 lg:min-w-[375px] lg:w-[35%] text-base text-foreground">
										<div className="flex justify-between items-center">
											<div className="flex-1 min-w-0 flex items-center gap-2 relative">
												<span className="truncate">
													{song.metadata.title || '-'}
												</span>
												<div
													className={cn(
														'items-center justify-center opacity-70 transition-opacity duration-200 shrink-0 cursor-pointer hover:opacity-100 group-hover:flex',
														shouldShowIcon ? 'flex' : 'hidden'
													)}
													onClick={handleIconClick}
												>
													{isCurrentlyPlaying ? (
														<Icon
															name="pause-circle-fill"
															width="24px"
															height="24px"
															color="var(--foreground)"
														/>
													) : (
														<Icon
															name="play-circle"
															width="24px"
															height="24px"
															color="var(--foreground)"
														/>
													)}
												</div>
											</div>
											<div className="lg:hidden flex items-center gap-3 shrink-0 pl-2">
												{/* Mobile: duration + feedback inline (after duration) */}
												<span className="text-sm shrink-0">
													{formatDuration(song.metadata.length)}
												</span>
												<div className="flex items-center gap-2">
													<button
														className={cn(
															'bg-transparent border-none cursor-pointer p-1 flex items-center justify-center text-transparent transition-all duration-200 hover:opacity-70 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed',
															isSubmittingFeedback &&
																isSelectedSong &&
																'cursor-not-allowed'
														)}
														onClick={(e) => {
															e.stopPropagation()
															handleFeedbackClick(song, true)
														}}
														disabled={isSubmittingFeedback}
													>
														{isSubmittingFeedback && isSelectedSong ? (
															<div className="w-4 h-4 border-2 border-[rgba(140,140,140,0.3)] border-t-[#8c8c8c] rounded-full animate-spin" />
														) : (
															<HandThumbsUp
																songFeedbacks={songFeedbacks}
																song={song}
																feedbackTypes={feedbackTypes || []}
															/>
														)}
													</button>
													<button
														className={cn(
															'bg-transparent border-none cursor-pointer p-1 flex items-center justify-center text-transparent transition-all duration-200 hover:opacity-70 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed',
															isSubmittingFeedback &&
																isSelectedSong &&
																'cursor-not-allowed'
														)}
														onClick={(e) => {
															e.stopPropagation()
															handleFeedbackClick(song, false)
														}}
														disabled={isSubmittingFeedback}
													>
														{isSubmittingFeedback && isSelectedSong ? (
															<div className="w-4 h-4 border-2 border-[rgba(140,140,140,0.3)] border-t-[#8c8c8c] rounded-full animate-spin" />
														) : (
															<HandThumbsDown
																songFeedbacks={songFeedbacks}
																song={song}
																feedbackTypes={feedbackTypes || []}
															/>
														)}
													</button>
												</div>
											</div>
										</div>
									</td>
									<td className="text-xs text-muted-foreground lg:text-base lg:p-4 lg:min-w-[250px] lg:w-[35%] block w-full truncate">
										{song.metadata.artist || '-'}
									</td>
									<td className="hidden lg:table-cell p-4 text-base text-foreground lg:min-w-[250px] lg:w-full">
										{formatDuration(song.metadata.length)}
									</td>
									<td className="hidden lg:table-cell p-4 text-base text-foreground lg:min-w-[250px] lg:w-full">
										{isLoadingFeedback ? (
											<Skeleton className="w-9 h-9 rounded-full" />
										) : feedbackIcon ? (
											<Popover
												open={hoveredFeedbackSongId === song.id}
												onOpenChange={(open) => {
													if (!open) {
														setHoveredFeedbackSongId(null)
													}
												}}
											>
												<PopoverTrigger asChild>
													<div
														className="flex items-center justify-center w-9 h-9 rounded-full cursor-pointer"
														style={{
															backgroundColor:
																getFeedbackBackground(feedbackCategory),
														}}
														onMouseEnter={() =>
															setHoveredFeedbackSongId(song.id)
														}
														onMouseLeave={() => setHoveredFeedbackSongId(null)}
													>
														<Icon
															name={feedbackIcon}
															width="36px"
															height="36px"
															color={getFeedbackIconColor()}
														/>
													</div>
												</PopoverTrigger>
												<PopoverContent
													side="right"
													align="start"
													className="p-0 w-auto min-w-[280px] border-0 bg-transparent shadow-none"
													onMouseEnter={() => setHoveredFeedbackSongId(song.id)}
													onMouseLeave={() => setHoveredFeedbackSongId(null)}
												>
													<div className="bg-[#242424] rounded-[8px] p-4 text-white min-w-[280px] block visible opacity-100 z-[9999]">
														{(() => {
															const stats = getSongFeedbackStats(song.id)

															// Always show content, even if counts are 0
															return (
																<>
																	<div className="flex flex-col gap-1 mb-2 visible">
																		<div className="text-white text-base font-medium block visible">
																			{stats.likesCount}{' '}
																			{stats.likesCount === 1
																				? t('entities.music.like', 'like')
																				: t('entities.music.likes', 'likes')}
																		</div>
																		<div className="text-white text-base font-medium block visible">
																			{stats.dislikesCount}{' '}
																			{stats.dislikesCount === 1
																				? t('entities.music.dislike', 'dislike')
																				: t(
																						'entities.music.dislikes',
																						'dislikes'
																					)}
																		</div>
																	</div>
																	{stats.dislikeReasons.length > 0 && (
																		<div className="flex flex-col gap-1 mt-2 visible">
																			{stats.dislikeReasons.map(
																				(
																					item: {
																						reason: string
																						count: number
																					},
																					index: number
																				) => (
																					<div
																						key={index}
																						className="block text-[#8c8c8c] text-xs visible"
																					>
																						<span className="text-[#8c8c8c] inline visible font-medium">
																							{item.count} {item.reason}
																						</span>
																					</div>
																				)
																			)}
																		</div>
																	)}
																</>
															)
														})()}
													</div>
												</PopoverContent>
											</Popover>
										) : null}
									</td>
									<td className="hidden lg:table-cell p-4 text-base text-foreground">
										{isLoadingFeedback ? (
											<div className="flex items-center justify-center gap-2">
												<Skeleton className="w-9 h-9 rounded-full" />
												<Skeleton className="w-9 h-9 rounded-full" />
											</div>
										) : (
											<div className="flex items-center justify-center gap-2">
												<button
													className={cn(
														'bg-transparent border-none cursor-pointer p-1 flex items-center justify-center text-transparent transition-all duration-200 hover:opacity-70 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed',
														isSubmittingFeedback &&
															isSelectedSong &&
															'cursor-not-allowed'
													)}
													onClick={(e) => {
														e.stopPropagation()
														handleFeedbackClick(song, true)
													}}
													disabled={isSubmittingFeedback}
												>
													{isSubmittingFeedback && isSelectedSong ? (
														<div className="w-4 h-4 border-2 border-[rgba(140,140,140,0.3)] border-t-[#8c8c8c] rounded-full animate-spin" />
													) : (
														<HandThumbsUp
															songFeedbacks={songFeedbacks}
															song={song}
															feedbackTypes={feedbackTypes || []}
														/>
													)}
												</button>
												<button
													className={cn(
														'bg-transparent border-none cursor-pointer p-1 flex items-center justify-center text-transparent transition-all duration-200 hover:opacity-70 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed',
														isSubmittingFeedback &&
															isSelectedSong &&
															'cursor-not-allowed'
													)}
													onClick={(e) => {
														e.stopPropagation()
														handleFeedbackClick(song, false)
													}}
													disabled={isSubmittingFeedback}
												>
													{isSubmittingFeedback && isSelectedSong ? (
														<div className="w-4 h-4 border-2 border-[rgba(140,140,140,0.3)] border-t-[#8c8c8c] rounded-full animate-spin" />
													) : (
														<HandThumbsDown
															songFeedbacks={songFeedbacks}
															song={song}
															feedbackTypes={feedbackTypes || []}
														/>
													)}
												</button>
											</div>
										)}
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			)}
		</div>
	)
}
